import React, { useEffect, useState, useRef, useContext } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Sparkles, ArrowRight, X } from 'lucide-react';
import { AuthContext } from '../App';

const TEAL = '#00A3A1';
const PINK = '#E94E89';

const STEPS = [
  {
    testId: 'nav-clients',
    title: 'Your Clients live here',
    body:
      'See every client at a glance, drill into their financials, exit plans, tasks and meetings. This is your home base.',
  },
  {
    testId: 'nav-tasks',
    title: 'Tasks & Boards',
    body:
      'Monday-style boards, Kanban and Timeline views. Assign work, track 6-minute time budgets and import jobs from a spreadsheet.',
  },
  {
    testId: 'nav-performance',
    title: 'Performance & FP&A',
    body:
      'Live P&L, forecasts, scenarios and an AI Copilot — built specifically for advisory consultancies.',
  },
  {
    testId: 'notification-bell',
    title: 'Stay in the loop',
    body:
      'Mentions, task updates and client activity surface here. Click the bell anytime to catch up.',
  },
];

const STORAGE_DONE = 'simplly_tour_done';
const SESSION_FLAG = 'simplly_show_welcome_tour';

export default function WelcomeTour() {
  const { user } = useContext(AuthContext) || {};
  const navigate = useNavigate();
  const location = useLocation();
  const [active, setActive] = useState(false);
  const [step, setStep] = useState(0);
  const [rect, setRect] = useState(null);
  const tickRef = useRef(0);

  // Decide whether to start the tour
  useEffect(() => {
    if (!user) return;
    if (localStorage.getItem(STORAGE_DONE) === '1') return;
    if (sessionStorage.getItem(SESSION_FLAG) !== '1') return;
    // Only start once we're inside the authenticated layout
    if (!location.pathname.startsWith('/dashboard')) {
      navigate('/dashboard', { replace: true });
      return;
    }
    setActive(true);
    setStep(0);
  }, [user, location.pathname, navigate]);

  // Position the tooltip next to the current step's anchor; retry until the
  // sidebar element exists in the DOM (sidebar may mount after a small delay).
  useEffect(() => {
    if (!active) return;
    let cancelled = false;
    const find = () => {
      if (cancelled) return;
      const target = STEPS[step]?.testId;
      const el = target && document.querySelector(`[data-testid="${target}"]`);
      if (el) {
        const r = el.getBoundingClientRect();
        setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
      } else if (tickRef.current < 30) {
        tickRef.current += 1;
        setTimeout(find, 200);
      }
    };
    tickRef.current = 0;
    setRect(null);
    find();
    const onScrollResize = () => {
      const target = STEPS[step]?.testId;
      const el = target && document.querySelector(`[data-testid="${target}"]`);
      if (el) {
        const r = el.getBoundingClientRect();
        setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
      }
    };
    window.addEventListener('resize', onScrollResize);
    window.addEventListener('scroll', onScrollResize, true);
    return () => {
      cancelled = true;
      window.removeEventListener('resize', onScrollResize);
      window.removeEventListener('scroll', onScrollResize, true);
    };
  }, [active, step]);

  const finish = () => {
    sessionStorage.removeItem(SESSION_FLAG);
    localStorage.setItem(STORAGE_DONE, '1');
    setActive(false);
  };

  const next = () => {
    if (step >= STEPS.length - 1) finish();
    else setStep(step + 1);
  };

  if (!active) return null;

  const cur = STEPS[step];
  const isLast = step === STEPS.length - 1;

  // Tooltip placement: pin just to the right of the anchor when possible,
  // otherwise center on screen.
  const tipStyle = rect
    ? {
        position: 'fixed',
        top: Math.min(window.innerHeight - 220, Math.max(20, rect.top - 4)),
        left: Math.min(window.innerWidth - 360, rect.left + rect.width + 18),
      }
    : {
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
      };

  // Highlight ring around the active anchor
  const ringStyle = rect
    ? {
        position: 'fixed',
        top: rect.top - 6,
        left: rect.left - 6,
        width: rect.width + 12,
        height: rect.height + 12,
        borderRadius: 14,
        boxShadow: `0 0 0 3px rgba(0,163,161,0.55), 0 0 0 9999px rgba(15,23,42,0.42)`,
        pointerEvents: 'none',
        transition: 'all 350ms cubic-bezier(.4,0,.2,1)',
        zIndex: 9000,
      }
    : null;

  return (
    <div
      data-testid="welcome-tour"
      style={{ position: 'fixed', inset: 0, zIndex: 8999, pointerEvents: 'none' }}
    >
      {/* Dimmer + ring */}
      {!ringStyle && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(15,23,42,0.42)',
            pointerEvents: 'auto',
          }}
          onClick={finish}
        />
      )}
      {ringStyle && <div style={ringStyle} />}

      {/* Tooltip card */}
      <div
        data-testid="welcome-tour-card"
        style={{
          ...tipStyle,
          width: 340,
          background: '#ffffff',
          borderRadius: 16,
          boxShadow: '0 24px 60px rgba(15,23,42,0.28)',
          padding: 22,
          pointerEvents: 'auto',
          animation: 'simplly-tour-pop 280ms cubic-bezier(.2,.9,.3,1.2) both',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
          <div
            style={{
              width: 28,
              height: 28,
              borderRadius: 8,
              background: `linear-gradient(135deg, ${TEAL}, ${PINK})`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#fff',
            }}
          >
            <Sparkles size={14} />
          </div>
          <span
            style={{
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: '0.6px',
              color: TEAL,
              textTransform: 'uppercase',
            }}
          >
            Step {step + 1} of {STEPS.length}
          </span>
          <button
            type="button"
            onClick={finish}
            data-testid="welcome-tour-skip"
            aria-label="Skip tour"
            style={{
              marginLeft: 'auto',
              border: 'none',
              background: 'transparent',
              color: '#94A3B8',
              cursor: 'pointer',
              padding: 2,
            }}
          >
            <X size={16} />
          </button>
        </div>

        <h3
          style={{
            margin: '0 0 6px',
            fontSize: 17,
            fontWeight: 700,
            color: '#0F172A',
          }}
        >
          {cur.title}
        </h3>
        <p style={{ margin: 0, fontSize: 13.5, lineHeight: 1.55, color: '#475569' }}>
          {cur.body}
        </p>

        {/* Progress dots */}
        <div style={{ display: 'flex', gap: 5, marginTop: 16 }}>
          {STEPS.map((_, i) => (
            <span
              key={i}
              style={{
                width: i === step ? 22 : 6,
                height: 6,
                borderRadius: 999,
                background: i <= step ? TEAL : '#E2E8F0',
                transition: 'all 250ms ease',
              }}
            />
          ))}
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 16 }}>
          <button
            type="button"
            onClick={finish}
            data-testid="welcome-tour-skip-link"
            style={{
              border: 'none',
              background: 'transparent',
              color: '#94A3B8',
              fontSize: 12.5,
              cursor: 'pointer',
              padding: 0,
            }}
          >
            Skip tour
          </button>
          <button
            type="button"
            onClick={next}
            data-testid="welcome-tour-next"
            style={{
              border: 'none',
              background: TEAL,
              color: '#fff',
              fontSize: 13,
              fontWeight: 600,
              padding: '8px 16px',
              borderRadius: 10,
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              boxShadow: '0 6px 16px rgba(0,163,161,0.30)',
            }}
          >
            {isLast ? 'Got it' : 'Next'}
            {!isLast && <ArrowRight size={14} />}
          </button>
        </div>
      </div>

      <style>{`
        @keyframes simplly-tour-pop {
          0%   { opacity: 0; transform: translateY(6px) scale(0.96); }
          100% { opacity: 1; transform: translateY(0) scale(1); }
        }
      `}</style>
    </div>
  );
}
